/**
 * @author Martin Leonov <martin.leonov@ki-wi.cz>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import isNil from 'lodash/isNil';

import FileSystemType from '../file/system/file-system-type';
import StorageTypesParser from '../session/storage-types-parser';

/**
 * @import { FileSystemUnit } from '../file/system'
 */

/**
 * @private
 * @typedef {{ [storageType in FileSystemType]?: FileSystemUnit[]; }} FileSystemUnitByType
 */

/**
 * Vyhledávač vhodné jednotky.
 */
export default class FileSystemUnitFinder {
  /**
   * Vytvoří novou instanci.
   * @param {boolean} useExternalUnits příkaz, který umožní používání externích jednotek.
   */
  constructor(useExternalUnits) {
    this.useExternalUnits = useExternalUnits;
  }

  /**
   * Vrátí první nalezenou interní jednotku.
   * @param {FileSystemUnit[]} fileSystemUnits pole fyzických jednotek.
   * @param {string | null} storage řetězec určující výběr úložiště.
   * @returns {Promise<FileSystemUnit | null>} nalezená fyzická jednotka.
   */
  find(fileSystemUnits, storage) {
    return new Promise((resolve) => {
      if (this.useExternalUnits && !isNil(storage)) {
        return resolve(this.findStorage(fileSystemUnits, storage));
      }

      return resolve(this.findInternal(fileSystemUnits));
    });
  }

  /**
   * Nalezne interní jednotku.
   * @param {FileSystemUnit[]} fileSystemUnits
   * @private
   * @returns {Promise<FileSystemUnit | null>}
   */
  findInternal(fileSystemUnits) {
    return new Promise((resolve) =>
      resolve(
        fileSystemUnits.find((u) => u.getType() === FileSystemType.Internal) ||
          null
      )
    );
  }

  /**
   * Nalezne vhodnou jednotku za základě řetezce storage.
   * @param {FileSystemUnit[]} fileSystemUnits
   * @param {string} storage
   * @private
   * @returns {Promise<FileSystemUnit | null>}
   */
  async findStorage(fileSystemUnits, storage) {
    const storagePriorities = StorageTypesParser.parse(storage);
    // Seskupení úložišť podle typu jednotky. Výsledek je objekt, kde klíčem
    // je typ úložiště a hodnotou je pole úložišť.
    // TODO: Do budoucna předělat na Object.groupBy:
    // - https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/groupBy
    const groupedPhysicalUnits = fileSystemUnits.reduce((acc, curr) => {
      const type = curr.getType();
      const value = acc[type];
      if (value) {
        value.push(curr);
      } else {
        acc[type] = [curr];
      }
      return acc;
    }, /** @type {FileSystemUnitByType} */ ({}));

    const suitableUnits = this.suitableUnits(
      storagePriorities,
      groupedPhysicalUnits
    );

    if (suitableUnits.length === 0) {
      return null;
    }

    const infoUnits = await Promise.all(
      suitableUnits.map((unit) => unit.getInfo())
    );
    // Vybereme jednotku s největším volným místem. V případě shody se bere první
    // jednotka v pořadí.
    const maxFreeIndex = infoUnits.reduce(
      (acc, { free }, i) => (free > infoUnits[acc].free ? i : acc),
      0
    );
    return suitableUnits[maxFreeIndex];
  }

  /**
   * Nalezne vhodné jednotky.
   * @param {FileSystemType[][]} storagePriorities
   * @param {FileSystemUnitByType} groupedPhysicalUnits
   * @private
   * @returns {FileSystemUnit[]}
   */
  suitableUnits(storagePriorities, groupedPhysicalUnits) {
    const physicalUnits = [];
    for (const storageGroup of storagePriorities) {
      for (const storage of storageGroup) {
        const storageArray = groupedPhysicalUnits[storage];
        if (Array.isArray(storageArray) && storageArray.length > 0) {
          physicalUnits.push(...storageArray);
        }
      }

      // Vrátí pouze jednotky z první skupiny
      if (physicalUnits.length > 0) {
        return physicalUnits;
      }
    }

    return physicalUnits;
  }
}
